import { GenericError } from '@cian/core/errors';
import { HttpCancelError } from '@cian/peperrors/shared';
import { TDevice } from '@cian/utils';
import { pathOr, equals } from 'ramda';
import { isNewPromoPinsAvailable } from 'shared/map-search/containers/Map/MapLayers/NewbuildingPromoPins/utils/isNewPromoPinsAvailable';
import { selectNewbuildingNewPromoPinsEnabled } from 'shared/map-search/selectors/newbuildingPromoFeatures';
import { getMapTelemetry } from 'shared/map-search/services/mapTelemetry';

import { assertJsonQueryValidity, hasZoomGroupChanged } from './helpers';
import { EResultsActionType, IFetchClustersSucceedParameters } from './types';
import { countClusters } from '../../mappers/clusters/dropInvalidClusters';
import { prepareClusters } from '../../mappers/clusters/prepareClusters';
import { fetchClusters as requestFetchClusters } from '../../services/clusters';
import { ITilesMap } from '../../types/map';
import { EErrorNotificationType } from '../../types/notifications';
import { TThunkAction } from '../../types/redux';
import { combineBboxes } from '../../utils/geometry';
import { mapBBoxBoundsToBBox } from '../../utils/mapBounds';
import { zoomToPrecision, precisionToMinZoom } from '../../utils/precision';
import { actionGenerator } from '../../utils/redux/actionGenerator';
import { getRequiredTiles, getVisibleTiles, tileToBbox } from '../../utils/tiles';
import { pushErrorNotification } from '../notifications';
import { setJsonQuery } from './setJsonQuery';
import { setMlSearchSessionGuid } from '../mlSearchSessionGuid';

const setStatusLoading = actionGenerator<EResultsActionType.Loading>(EResultsActionType.Loading);
const setStatusSucceed = actionGenerator<EResultsActionType.Succeed, IFetchClustersSucceedParameters>(
  EResultsActionType.Succeed,
);
const setStatusFailed = actionGenerator<EResultsActionType.Failed>(EResultsActionType.Failed);
let prevBounds: [YMaps.TCoord, YMaps.TCoord] | undefined;
let prevZoom: number | undefined;

interface IFetchClustersParameters {
  updateBounds?: boolean;
  deviceType?: TDevice;
  jsonQuery: TJsonQuery;
}

export function fetchClusters({
  updateBounds = false,
  deviceType,
  jsonQuery,
}: IFetchClustersParameters): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const {
      config,
      custom: { subdomain },
      logger,
    } = context;
    const mapTelemetry = getMapTelemetry();
    const state = getState();

    const {
      features,
      mapBounds,
      detailsVisiblePrecision,
      results: { jsonQuery: resultsJsonQuery },
      mapContainerArea,
      clustersType,
    } = state;

    const isNewPromoPinsEnabled = selectNewbuildingNewPromoPinsEnabled(state);

    const { bounds, zoom: currentZoom } = mapBounds;
    const isBoundsEquals = equals(bounds, prevBounds);
    const roundedCurrentZoom = Math.round(currentZoom);
    const zoomGroupChanged = hasZoomGroupChanged({
      currentZoom: roundedCurrentZoom,
      previousZoom: prevZoom,
      config,
      jsonQuery,
    });
    prevBounds = bounds;
    prevZoom = roundedCurrentZoom;

    if (!bounds || !currentZoom) {
      logger.error('Either bounds or zoom is undefined', { domain: 'actions/results/fetchClusters' });

      return;
    }

    const clean = !equals(jsonQuery, resultsJsonQuery) || zoomGroupChanged;

    const precision = zoomToPrecision(config, currentZoom);
    const zoom = precisionToMinZoom(config, precision);
    const visibleTiles = getVisibleTiles(bounds, zoom);

    let requiredTiles = visibleTiles;

    const isNewbuildingNewPromoPinsEnabled = isNewPromoPinsAvailable({
      precision,
      detailsVisiblePrecision,
      currentZoom,
      jsonQuery,
      isNewPromoPinsEnabled,
    });

    if (isNewbuildingNewPromoPinsEnabled && !clean && isBoundsEquals) {
      logger.debug('Unfortunately we tried to fetch already loaded tiles', {
        domain: 'actions/results/fetchClusters',
      });

      return;
    }

    if (!isNewbuildingNewPromoPinsEnabled && !clean && !clustersType) {
      const loadedTiles = pathOr<ITilesMap>({}, [precision, 'tiles'], features);

      requiredTiles = getRequiredTiles(loadedTiles, visibleTiles);
      if (requiredTiles.length === 0) {
        logger.debug('Unfortunately we tried to fetch already loaded tiles', {
          domain: 'actions/results/fetchClusters',
        });

        return;
      }
    }

    dispatch(setJsonQuery(jsonQuery));
    dispatch(setStatusLoading());

    /** Фиксирует, сколько из бюджета SLI 2 съел наш троттлинг до старта запроса (решение Q3) */
    mapTelemetry.trackPinsUpdateRequestStarted();

    const requestStartedAt = performance.now();

    try {
      const requiredBboxes = requiredTiles.map(t => tileToBbox(t, zoom));
      const combinedBbox = combineBboxes(requiredBboxes);
      const clustersData = await requestFetchClusters(context, {
        subdomain,
        onRetry: mapTelemetry.trackClustersRetried,
        params: {
          zoom: updateBounds ? undefined : zoom,
          currentZoom,
          bbox: updateBounds ? undefined : [mapBBoxBoundsToBBox({ bounds: combinedBbox })],
          jsonQuery,
          extended: !updateBounds,
          mapContainerArea: mapContainerArea || undefined,
        },
      });

      mapTelemetry.trackClustersRequestDuration(performance.now() - requestStartedAt);

      const clusters = prepareClusters({
        data: clustersData,
        mapBounds,
        currentZoom,
      });

      /** Пины, отброшенные подготовкой, и есть пришедшие с непригодными данными (SLI 8 и 9) */
      mapTelemetry.trackPinsData(countClusters(clustersData), countClusters(clusters));

      assertJsonQueryValidity(logger, jsonQuery, clusters.jsonQuery);

      dispatch(setMlSearchSessionGuid(clustersData.mlSearchSessionGuid || null));
      dispatch(
        setStatusSucceed({
          ...clusters,
          /** Сейчас делаем эксп только на десктопе, он определяется наличием поля clustersType */
          clustersType: deviceType === 'desktop' ? clusters.clustersType : null,
          jsonQuery,
          clean,
          tiles: requiredTiles,
          updateBounds,
        }),
      );

      mapTelemetry.trackClustersSucceed();
    } catch (error) {
      let details: any;

      if (
        error instanceof HttpCancelError ||
        ((details = (error as GenericError).details) &&
          details instanceof DOMException &&
          details.name === 'AbortError')
      ) {
        /**
         * Отмена — это не отказ: пользователь просто продолжил двигать карту.
         * Считаем отдельно, чтобы видеть объём отмен, но в success/fail не смешиваем.
         */
        mapTelemetry.trackClustersCancelled();

        return;
      }

      logger.error(error);

      /**
       * Длительность отказа не менее интересна, чем успешная: запрос, упавший по таймауту через
       * десяток секунд, объясняет покрасневший SLI лучше любого счётчика.
       */
      mapTelemetry.trackClustersRequestDuration(performance.now() - requestStartedAt);
      mapTelemetry.trackClustersFailed(error);

      dispatch(setStatusFailed());
      dispatch(pushErrorNotification(EErrorNotificationType.FetchClusters));
    }
  };
}
